import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiExternalLink, FiInfo, FiSunrise, FiWind, FiAnchor } = FiIcons;

const attractions = [
  {
    name: "Rizal Park",
    distance: "1 min walk",
    desc: "A historic public park located in the heart of Lucban, perfect for leisurely walks and experiencing the local atmosphere right across the street.",
    icon: FiMapPin,
    image_url: "https://images.unsplash.com/photo-1496560230298-753f851655c3?auto=format&fit=crop&q=80&w=800"
  },
  {
    name: "Marcos Tigla Park",
    distance: "2 min walk",
    desc: "A serene community park that offers a peaceful escape and serves as a gathering spot for locals and visitors alike in the town center.",
    icon: FiWind,
    image_url: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&q=80&w=800"
  },
  {
    name: "St Louis Bishop Parish Church",
    distance: "2 min walk",
    desc: "A 16th-century stone marvel and the historic heart of Lucban, famous for its intricate architecture and spiritual significance.",
    icon: FiAnchor,
    image_url: "https://images.unsplash.com/photo-1548625315-05537554900a?auto=format&fit=crop&q=80&w=800"
  },
  {
    name: "Kamay ni Hesus",
    distance: "5 min drive",
    desc: "A world-renowned 5-hectare pilgrimage site featuring the healing chapel and the iconic 50-foot statue of the Ascending Christ atop 300+ steps.",
    icon: FiSunrise,
    image_url: "https://images.unsplash.com/photo-1621274098552-257a0701602e?auto=format&fit=crop&q=80&w=800"
  },
  {
    name: "Bukid Amara",
    distance: "10 min drive",
    desc: "A picturesque farm recreation site and flower paradise at the foot of Mt. Banahaw, perfect for scenic strolls and vibrant photography.",
    icon: FiInfo,
    image_url: "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?auto=format&fit=crop&q=80&w=800"
  }
];

const AttractionCard = ({ attraction, index }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="group bg-white border border-brand-olive/5 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-500"
    >
      <div className="relative h-56 w-full overflow-hidden bg-brand-olive/5">
        {!imageError && attraction.image_url ? (
          <>
            <img 
              src={attraction.image_url} 
              alt={attraction.name}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              onError={() => setImageError(true)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-olive/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-brand-cream/50">
            <SafeIcon icon={attraction.icon} className="text-brand-olive text-4xl opacity-40" />
          </div>
        )}
        
        <div className="absolute top-4 right-4">
          <span className="text-[10px] font-black uppercase tracking-widest text-white bg-brand-olive/80 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-lg">
            {attraction.distance}
          </span>
        </div>
      </div>

      <div className="p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-full bg-brand-olive/10 flex items-center justify-center shrink-0">
            <SafeIcon icon={attraction.icon} className="text-brand-olive text-sm" />
          </div>
          <h3 className="font-serif text-xl uppercase tracking-widest text-brand-olive truncate">{attraction.name}</h3>
        </div>
        <p className="text-brand-olive/70 text-sm leading-relaxed font-light">
          {attraction.desc}
        </p>
      </div>
    </motion.div>
  );
};

const Location = () => {
  const mapEmbedUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3873.351234907101!2d121.55403277509205!3d14.11545698631168!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x33bd53c65c697793%3A0x6479017770e5b7b6!2sR.%20Venue!5e0!3m2!1sen!2sph!4v1716200000000!5m2!1sen!2sph";

  return (
    <div className="w-full">
      <section className="pt-40 pb-20 bg-brand-olive text-white text-center">
        <div className="container mx-auto px-6">
          <p className="font-script text-3xl text-brand-cream mb-2">Finding Your Way</p>
          <h1 className="font-serif text-4xl md:text-6xl uppercase tracking-[0.2em]">Our Location</h1>
        </div>
      </section>

      <section className="py-24 bg-brand-cream">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="inline-block px-4 py-1.5 bg-brand-olive/5 text-brand-olive text-[10px] font-bold uppercase tracking-widest mb-6 border border-brand-olive/10">
                Central District
              </div>
              <h2 className="font-serif text-3xl md:text-4xl uppercase tracking-widest text-brand-olive mb-6">
                In the heart of <br />Lucban, Quezon
              </h2>
              <p className="text-brand-olive/80 text-lg mb-8 leading-relaxed font-light">
                R. Venue Apartelle stands as a contemporary landmark at the corner of Quezon Avenue and Avenida Rizal. Located on the 3rd floor, we offer a peaceful vantage point over the bustling town center.
              </p>

              <div className="space-y-6 mb-10">
                <div className="group flex items-start gap-5 p-6 bg-white shadow-sm border border-brand-olive/5">
                  <div className="w-12 h-12 bg-brand-olive/10 rounded-full flex items-center justify-center shrink-0">
                    <SafeIcon icon={FiMapPin} className="text-brand-olive" />
                  </div>
                  <div>
                    <h4 className="font-serif font-bold text-brand-olive uppercase tracking-widest text-xs mb-1">Exact Address</h4>
                    <p className="text-brand-olive/70 text-sm leading-relaxed">
                      3rd Floor R.Venue Bldg.<br />
                      Quezon Ave Cor Avenida Rizal<br />
                      Lucban, Quezon 4328<br />
                      Philippines
                    </p>
                  </div>
                </div>
              </div>

              <a 
                href="https://maps.app.goo.gl/3fX8Uo3vX6p5zN7s7" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-primary flex items-center gap-3 w-fit"
              >
                Open in Google Maps <SafeIcon icon={FiExternalLink} />
              </a>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative h-[500px] lg:h-[600px]">
              <div className="w-full h-full shadow-2xl border-[12px] border-white overflow-hidden rounded-2xl">
                <iframe src={mapEmbedUrl} width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" title="R. Venue Location Map" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-4xl uppercase tracking-widest text-brand-olive mb-4">
              Nearby Attractions
            </h2>
            <p className="text-brand-olive/70 text-lg font-light max-w-2xl mx-auto italic">
              “Explore the rich heritage and natural beauty of Lucban, all within easy reach from our central location.”
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {attractions.map((attraction, index) => (
              <AttractionCard key={index} attraction={attraction} index={index} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Location;